import { useEffect } from 'react'
import { RIGHT_RAIL_WIDTH } from '@/components/layout/right-rail-constants'
import { X, Menu, MessageSquarePlus, PictureInPicture2, SquarePen } from 'lucide-react'
import { useChatStore } from '@/stores/chat-store'
import { useThreadStore } from '@/stores/thread-store'
import { usePanelInsightsStore } from '@/stores/panel-insights-store'
import { HistoryBriefing } from '@/features/history/components/history-briefing'
import { CompanionComposer } from './companion-composer'
import { useChatTickerDrop } from './use-chat-ticker-drop'
import { TickerDropOverlay } from '@/features/ticker-drag/ticker-drop-overlay'
import { CompanionCreditsLine } from './companion-credits-line'
import { CompanionHistoryPanel } from './companion-history-panel'
import { ChatConversationBody, ChatActionBanners } from './chat-conversation-body'
import { useCompanionConversation, type CompanionConversation } from './use-companion-conversation'

// Shared with the panel config rail so the two swap in place.
const DRAWER_WIDTH = RIGHT_RAIL_WIDTH

interface ChatDrawerShellProps {
  conversation: CompanionConversation
  /**
   * Whether the drawer is the companion's current surface. When another shell
   * (the floating window) owns the conversation, the drawer stays mounted but
   * collapsed to width 0, exactly like its closed state.
   */
  active?: boolean
}

/**
 * The side-drawer shell: width-animated right panel with the history
 * dropdown, pop-out and close controls. The conversation itself (message
 * sync, persistence, send handlers) lives in useCompanionConversation and is
 * passed in by the host so the drawer and the floating window share it.
 */
export function ChatDrawerShell({ conversation, active = true }: ChatDrawerShellProps) {
  const storeOpen = useChatStore((s) => s.open)
  const setOpen = useChatStore((s) => s.setOpen)
  const setSurface = useChatStore((s) => s.setSurface)
  const isOpen = storeOpen && active
  const { isProcessing, page, handleSend, handleNewConversation, handleSelectConversation } =
    conversation
  const setHistoryOpen = useThreadStore((s) => s.setHistoryOpen)

  // Ask AI rail briefing (History only): show the aggregated panel insights +
  // seeded follow-up context in the empty state, before a conversation starts.
  const chatContext = useChatStore((s) => s.context)
  // Pre-fill draft (e.g. "AAPL on 2024-03-01: " from a chart's "Ask AI about
  // this day") — the input seeds from it once, then clears it.
  const draft = useChatStore((s) => s.draft)
  const setDraft = useChatStore((s) => s.setDraft)
  const panelInsightCount = usePanelInsightsStore((s) => Object.keys(s.insights).length)
  const hasBriefing = page === 'history' && (panelInsightCount > 0 || chatContext != null)
  const tickerDrop = useChatTickerDrop(isOpen)

  // ── ESC closes the drawer ────────────────────────────────
  // Small a11y affordance; the drawer is a side panel (not a modal), so no
  // focus trap — just a keyboard escape hatch.
  useEffect(() => {
    if (!isOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [isOpen, setOpen])

  return (
    <div
      data-companion-surface
      role="complementary"
      aria-label="Edge AI chat"
      className="relative z-[60] flex shrink-0 flex-col overflow-hidden border-l border-border bg-background transition-[width] duration-300 ease-in-out"
      style={{ width: isOpen ? DRAWER_WIDTH : 0, borderLeftWidth: isOpen ? 1 : 0 }}
    >
      {!isOpen ? null : (
        <div className="relative flex h-full w-[360px] flex-col" {...tickerDrop.dropHandlers}>
          <TickerDropOverlay
            isDragging={tickerDrop.isDragging}
            isOver={tickerDrop.isOver}
            label={`Ask about $${tickerDrop.ticker ?? ''}`}
          />
          {/* History slide-over — opens from the hamburger, inside the drawer */}
          <CompanionHistoryPanel
            onSelectThread={handleSelectConversation}
            onNewThread={handleNewConversation}
          />

          {/* Header */}
          {/* 1fr|auto|1fr grid so the wordmark is PERFECTLY centered in the
              panel regardless of how many controls flank it. */}
          <div className="grid h-10 shrink-0 grid-cols-[1fr_auto_1fr] items-center px-2">
            {/* Left: the hamburger (conversation history) and New chat.
                Right: float the window, then close (Chris, 2026-09-30). The
                browser-window popout is hidden (Chris, 2026-08-29). */}
            <div className="flex items-center justify-start gap-0.5">
              <button
                onClick={() => setHistoryOpen(true)}
                className="flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-hover hover:text-foreground"
                title="Conversations"
                aria-label="Conversation history"
              >
                <Menu size={15} />
              </button>
              <button
                onClick={handleNewConversation}
                className="flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-hover hover:text-foreground"
                title="New chat"
                aria-label="New chat"
              >
                <SquarePen size={14} />
              </button>
            </div>

            {/* Center: the wordmark. */}
            <span className="flex items-center gap-1.5">
              <span className="text-sm font-semibold text-foreground">Edge AI</span>
              {/* Same pill as the nav sidebar's Beta tag (betaTagBase). */}
              <span className="rounded-sm bg-primary px-1.5 py-0.5 text-[10px] font-semibold uppercase leading-none tracking-wide text-primary-foreground">
                Beta
              </span>
            </span>

            <div className="flex items-center justify-end gap-0.5">
              <button
                onClick={() => setSurface('floating')}
                className="flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-hover hover:text-foreground"
                title="Float window"
                aria-label="Float window"
              >
                <PictureInPicture2 size={14} />
              </button>
              <button
                onClick={() => setOpen(false)}
                className="flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-hover hover:text-foreground"
                title="Close"
                aria-label="Close chat"
              >
                <X size={15} />
              </button>
            </div>
          </div>

          {/* Messages */}
          <ChatConversationBody
            conversation={conversation}
            visible={isOpen}
            emptyState={
              hasBriefing ? (
                <HistoryBriefing onAsk={handleSend} />
              ) : (
                <div className="flex h-full flex-col items-center justify-center gap-2 p-6 text-center">
                  <div className="rounded-full bg-muted p-3">
                    <MessageSquarePlus size={20} className="text-muted-foreground" />
                  </div>
                  <p className="text-sm font-medium text-foreground">Start a conversation</p>
                  <p className="text-xs text-muted-foreground">
                    Ask about strategies, analyze results, or get help with your setup.
                  </p>
                </div>
              )
            }
          />

          <ChatActionBanners />

          {/* Unified composer + the metered-credits line below it */}
          <CompanionComposer
            variant="drawer"
            onSend={handleSend}
            disabled={isProcessing}
            placeholder="Message Edge AI..."
            seedText={draft}
            onSeedConsumed={() => setDraft(null)}
          />
          <CompanionCreditsLine />
        </div>
      )}
    </div>
  )
}

/**
 * Self-contained drawer: owns the conversation AND the shell. Used when the
 * drawer is the only companion surface (and by its unit tests). Do NOT mount
 * this alongside CompanionHost — the conversation hook must run exactly once.
 */
export function ChatDrawer() {
  const conversation = useCompanionConversation()
  return <ChatDrawerShell conversation={conversation} />
}
